import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EventModalMediaContent from '../../../../../components/EventModalMediaContent';
import { useOrderContext } from '../../../../../providers/OrderProvider';
import createMarkup from '../../../../../utils/createMarkup';
import { focusOrderProductRow } from '../../../../../utils/order/focusOrderProductRow';

const normalizeReasonHtml = (html) =>
	String(html || '')
		.replace(/<p>\s*\[订单申请\][\s\S]*?<\/p>/i, '')
		.replace(/触发原因/g, '审批事项')
		.replace(/<\/?strong>/gi, '')
		.trim();

const MatterBullet = ({ children }) => (
	<li>
		<span className='me-3'>
			<span className='d-inline-block rounded-circle bg-300 me-2' style={{ width: 6, height: 6, verticalAlign: 'middle' }} />
			{children}
		</span>
	</li>
);

MatterBullet.propTypes = {
	children: PropTypes.node,
};

const OrderApprovalReasonDetail = ({
	reason: reasonProp,
	detail: detailProp,
	embedded = false,
	onBeforeLocate,
	showLocateArrow = true,
}) => {
	const { order } = useOrderContext();
	const approval = order?.approval && typeof order.approval === 'object' ? order.approval : null;
	const detail = detailProp && typeof detailProp === 'object' ? detailProp : approval?.detail;
	const triggers = Array.isArray(detail?.triggers) ? detail.triggers : [];
	const belowMinimumItems = Array.isArray(detail?.belowMinimumItems) ? detail.belowMinimumItems : [];
	const reasonHtml = normalizeReasonHtml(reasonProp || approval?.reason);

	const locateProduct = (item) => {
		const run = () =>
			focusOrderProductRow({
				itemIndex: Number.isInteger(item.itemIndex) ? item.itemIndex : undefined,
				productCode: item.productCode,
			});
		if (typeof onBeforeLocate === 'function') {
			onBeforeLocate();
			window.setTimeout(run, 360);
			return;
		}
		run();
	};

	const matterContent =
		triggers.length > 0 ? (
			<div className='d-flex flex-column gap-1'>
				{!embedded ? <p className='mb-0 fs-10 fw-normal'>审批事项：</p> : null}
				<ul className='fs-10 fw-normal mb-0 list-unstyled'>
					{triggers.includes('belowMinimumPrice') ? (
						<MatterBullet>
							<span className='text-600'>产品销售价格低于底价</span>
							{belowMinimumItems.length > 0 ? (
								<div className='d-flex flex-column gap-1 mt-1 ps-3'>
									{belowMinimumItems.map((item, index) => {
										const label = item.productCode || item.name || '未命名产品';
										const nameHint = item.productCode && item.name ? `（${item.name}）` : '';
										return (
											<button
												key={`${label}-${index}`}
												type='button'
												className='btn btn-link p-0 text-start text-decoration-none d-inline-flex align-items-center gap-1 fs-10 fw-normal text-primary'
												title='定位到产品列表'
												aria-label={`定位到产品 ${label}`}
												onClick={() => locateProduct(item)}
											>
												<span>
													{label}
													{nameHint}
												</span>
												{showLocateArrow ? <FontAwesomeIcon icon='arrow-down' className='fs-11' /> : null}
											</button>
										);
									})}
								</div>
							) : null}
						</MatterBullet>
					) : null}
					{triggers.includes('partialPayment') ? (
						<MatterBullet>
							<span className='text-600'>支付方式为部分支付</span>
						</MatterBullet>
					) : null}
					{triggers.includes('creditOverdue') ? (
						<MatterBullet>
							<span className='text-600'>额度支付有单笔超期</span>
						</MatterBullet>
					) : null}
				</ul>
			</div>
		) : reasonHtml ? (
			<div className='approval-reason-content fs-10 fw-normal text-900 mb-0' dangerouslySetInnerHTML={createMarkup(reasonHtml)} />
		) : (
			<p className='mb-0 fs-10 fw-normal text-600'>暂无审批说明</p>
		);

	if (embedded) {
		return matterContent;
	}

	return (
		<EventModalMediaContent icon='clipboard-check' heading='审批说明'>
			{matterContent}
		</EventModalMediaContent>
	);
};

OrderApprovalReasonDetail.propTypes = {
	reason: PropTypes.string,
	detail: PropTypes.object,
	embedded: PropTypes.bool,
	onBeforeLocate: PropTypes.func,
	showLocateArrow: PropTypes.bool,
};

export default OrderApprovalReasonDetail;
